import { Badge, TableColumn } from '@ibsys/kit';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getSourceTypeLabel, type KnowledgeSourceListItem, SOURCE_TYPE_LABELS } from '@/entities/knowledgeSources';
import { Icon, IconButton, TruncatedText } from '@/shared/ui';
import { getFileTypeConfig } from './fileTypes';

/** Обозначение незаполненного значения — то же, что и в остальных списках */
const EMPTY_CELL = '—';

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

type Params = {
  /** Переход к нодам, наполненным из источника */
  onGoToNodes: (sourceId: number) => void;
};

/**
 * Колонки списка источников знаний. Каждая колонка со своим значением упорядочивается в обе
 * стороны — сервер отбирает и упорядочивает по тем же значениям, которые здесь показаны.
 */
export const useKnowledgeSourceColumns = ({ onGoToNodes }: Params) => {
  const { t } = useTranslation(['knowledgeSources']);

  // Опции отбора по типу — тот же набор значений, которым колонка подписывает источники:
  // показанный тип должен годиться в условие, иначе часть источников отобрать нечем
  const sourceTypeOptions = useMemo(
    () =>
      SOURCE_TYPE_LABELS.map((type) => ({
        value: type,
        label: t(`knowledgeSources:fileTypes.${type}`),
      })),
    [t],
  );

  return useMemo(
    () =>
      [
        {
          accessorKey: 'fileName',
          header: t('knowledgeSources:table.fileName'),
          size: 250,
          minSize: 150,
          enableSorting: true,
          cell: ({ row }) => (
            <div className="flex items-start gap-2">
              {getFileTypeConfig(row.original.sourceType).icon}
              <TruncatedText lines={2} className="break-all">
                {row.original.file.fileName}
              </TruncatedText>
            </div>
          ),
        },
        {
          accessorKey: 'sourceType',
          header: t('knowledgeSources:table.type'),
          minSize: 100,
          maxSize: 150,
          enableSorting: true,
          filters: {
            type: 'select',
            options: sourceTypeOptions,
          },
          cell: ({ row }) => {
            const { sourceType } = row.original;
            const typeLabel = t(`knowledgeSources:fileTypes.${getSourceTypeLabel(sourceType)}`);
            return (
              <Badge variant="secondary" outline className={getFileTypeConfig(sourceType).badgeClass}>
                {typeLabel}
              </Badge>
            );
          },
        },
        {
          accessorKey: 'size',
          header: t('knowledgeSources:table.size'),
          minSize: 80,
          maxSize: 120,
          enableSorting: true,
          cell: ({ row }) => {
            const { size } = row.original.file;
            if (size == null) return <span className="text-muted-foreground">{EMPTY_CELL}</span>;
            return formatFileSize(size);
          },
        },
        {
          accessorKey: 'createdAt',
          header: t('knowledgeSources:table.uploadDate'),
          minSize: 120,
          maxSize: 160,
          enableSorting: true,
          filters: { type: 'date-range' },
          cell: ({ row }) => format(new Date(row.original.createdAt), 'dd.MM.yyyy'),
        },
        {
          header: '',
          accessorKey: 'id',
          minSize: 80,
          maxSize: 100,
          enableResizing: false,
          pinned: 'right',
          cell: ({ row }) => (
            <div className="flex w-full justify-end gap-2">
              <IconButton
                size="icon"
                className={'size-6'}
                icon={<Icon type="arrow-long-right" className={'size-3'} />}
                variant="secondary"
                label={t('knowledgeSources:goToNodes')}
                onClick={() => onGoToNodes(row.original.id)}
              />
            </div>
          ),
        },
      ] satisfies TableColumn<KnowledgeSourceListItem>[],
    [t, sourceTypeOptions, onGoToNodes],
  );
};
